import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Smartphone, Save, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function SmsRemindersCard({ onChange }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/tenant/profile").then((r) => setP(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/tenant/profile", { phone: p.phone, sms_carrier: p.sms_carrier, sms_opt_in: p.sms_opt_in });
      setP(data); toast.success(data.sms_ready ? "Text reminders are on" : "Saved"); onChange?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!p) return null;
  return (
    <div className={`rounded-2xl border p-6 ${p.sms_ready ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`} data-testid="sms-reminders-card">
      <div className="flex items-center gap-2 mb-1">
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${p.sms_ready ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}><Smartphone className="h-5 w-5" /></span>
        <h2 className="font-display font-bold text-lg text-slate-900">Rent reminders by text</h2>
        <Switch checked={p.sms_opt_in} onCheckedChange={(v) => set("sms_opt_in", v)} className="ml-auto" data-testid="sms-opt-in" />
      </div>
      <p className="text-sm text-slate-600">Get a text a few days before rent is due and on the due day — with a one-tap pay link. Reminders also go to your email.</p>
      <div className={`grid sm:grid-cols-2 gap-3 mt-4 ${p.sms_opt_in ? "" : "opacity-50"}`}>
        <div className="space-y-1.5"><Label className="text-xs">US mobile number</Label><Input value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" disabled={!p.sms_opt_in} data-testid="sms-phone" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Carrier</Label>
          <Select value={p.sms_carrier || ""} onValueChange={(v) => set("sms_carrier", v)} disabled={!p.sms_opt_in}>
            <SelectTrigger data-testid="sms-carrier"><SelectValue placeholder="Pick your carrier" /></SelectTrigger>
            <SelectContent>{p.carriers.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Button onClick={save} disabled={saving} className="gap-2" data-testid="sms-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button>
        {p.sms_ready && <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1" data-testid="sms-ready"><CheckCircle2 className="h-3.5 w-3.5" /> Texts on</span>}
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Delivered through your carrier's free email-to-SMS gateway — standard message rates may apply.</p>
    </div>
  );
}
